import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api } from './api.ts';

export const useProjects = () => useQuery({ queryKey: ['projects'], queryFn: () => api.projects(), staleTime: 30_000 });
export const useLabels = (project?: string) => useQuery({ queryKey: ['labels', project ?? null], queryFn: () => api.labels(project), staleTime: 30_000 });
export const useUsers = () => useQuery({ queryKey: ['users'], queryFn: api.users, staleTime: 60_000 });
/** Histórias (abertas por padrão) de um projeto ou de todos. */
export const useStories = (project?: string, includeClosed = false) =>
  useQuery({ queryKey: ['tasks', 'stories', project ?? null, includeClosed], queryFn: () => api.tasks({ type: 'story', project, includeClosed: includeClosed || undefined, sort: 'key', order: 'desc', limit: 200 }), staleTime: 30_000 });
/** Épicos abertos (de todos os projetos: um épico pode reunir histórias de vários). */
export const useEpics = (includeClosed = false) =>
  useQuery({ queryKey: ['tasks', 'epics', includeClosed], queryFn: () => api.tasks({ type: 'epic', includeClosed: includeClosed || undefined, sort: 'key', order: 'desc', limit: 200 }), staleTime: 30_000 });
export const useReleases = (project?: string, status?: string) =>
  useQuery({ queryKey: ['releases', project ?? '', status ?? ''], queryFn: () => api.releases({ project, status }), staleTime: 30_000 });
export const useSprints = (project?: string, status?: string) =>
  useQuery({ queryKey: ['sprints', project ?? '', status ?? ''], queryFn: () => api.sprints({ project, status }), staleTime: 30_000 });
export const useTemplates = (project?: string) => useQuery({ queryKey: ['templates', project ?? ''], queryFn: () => api.templates(project), staleTime: 60_000 });
export const useAgents = () => useQuery({ queryKey: ['agents'], queryFn: api.agents, staleTime: 60_000 });

/** Filtros guardados na URL (compartilháveis e persistem no recarregar). */
export function useUrlFilters<K extends string>(keys: readonly K[]) {
  const [params, setParams] = useSearchParams();
  const values = Object.fromEntries(keys.map((k) => [k, params.get(k) ?? ''])) as Record<K, string>;
  const set = useCallback(
    (patch: Partial<Record<K, string>>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch) as [string, string | undefined][]) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  return [values, set] as const;
}

export function useToast() {
  const [toast, setToast] = useState<{ message: string; kind: 'success' | 'error' } | null>(null);
  const show = useCallback((message: string, kind: 'success' | 'error' = 'success') => setToast({ message, kind }), []);
  const clear = useCallback(() => setToast(null), []);
  return { toast, show, clear };
}
